import type { QueryClient } from '@tanstack/react-query';

/**
 * What a write changes, named as cache keys, in one place — and which half of it an operator's
 * button is allowed to wait for.
 *
 * Three defects came out of not having this:
 *
 *  1. A screen and the hook it calls disagreed about what a write touches. The manufacturing
 *     board moves an order with the order's own transition route but re-read only its own board,
 *     while `useOrderTransition` re-read only the order keys — so whichever screen you were not
 *     looking at kept the pre-write answer, and the admin order detail's "Create consignment
 *     invoice" button (which comes from the *candidates* query) did not appear after the order
 *     reached READY.
 *  2. The invalidations were fired and forgotten (`void qc.invalidateQueries(...)`), so the
 *     mutation resolved, its button re-enabled and the screen re-rendered — all before the
 *     re-read landed. An operator saw the same button over the same old status and clicked again.
 *  3. The cure for (2) overshot into a worse defect. `onSuccess` returned one `invalidateAll`
 *     over all three broad prefixes, and `invalidateQueries` resolves only once EVERY matching
 *     active query has refetched. One slow, retrying or never-settling query among the dozens
 *     under `['portal', p, 'orders']` held the button on "Working…" for good — over a write the
 *     database had already taken. Observed on three buttons, every one of them coming through
 *     this single await: "Confirm order" and "Begin manufacturing" on A-15, and "Acknowledge"
 *     on A-26.
 *
 *     There was a SECOND copy of the same await on the consignment-invoice side, which the first
 *     pass missed: `invalidateConsignmentInvoices`, plus the two shipment hooks, awaited their own
 *     three prefixes uncapped. "Create consignment invoice" and "Create shipment" hung the same way,
 *     over PI-000035 and SH-1031 that the database already had. Both families now go through
 *     `settleWrite`, which is why the write-key sets below come in pairs: the full set, and what
 *     each acting screen reads its own answer from.
 *
 * So a write now re-reads in two halves, and (3) cannot come back:
 *
 *   `settle`      the handful of entries the screen being looked at reads its OWN new answer
 *                 from. Awaited, because that is defect (2): the button must not come back over
 *                 the pre-write status.
 *   `background`  everything else the write changed. Invalidated exactly as before — that is
 *                 defect (1), nothing goes un-re-read — but never awaited, so a query belonging
 *                 to a screen nobody is looking at cannot hold this screen's button.
 *
 * And the awaited half is capped by `SETTLE_DEADLINE_MS`. The narrow set is narrower, not
 * immortal: a stuck button has to be impossible, not unlikely. Past the cap the button settles
 * and the re-read still lands when it lands — the invalidation is recorded before any of the
 * waiting starts, so nothing is lost, only no longer waited for.
 *
 * Keys here are PREFIXES. `['portal', p, 'orders']` covers the queue, every order detail and the
 * per-order payments, milestones, shipments and invoice entries hanging off it, because
 * `invalidateQueries` matches a key against the start of each cached key.
 */

/** The shape `useScope` gives every admin key: the portal in front of it (SC-02). */
export const portalKey = (portal: string | null, key: readonly unknown[]): unknown[] => ['portal', portal, ...key];

/** A-26's board. Its own key carries the search term after this, which the prefix covers. */
export const manufacturingBoardKey = (portal: string | null): unknown[] => portalKey(portal, ['manufacturing', 'board']);

/**
 * Everything an admin write against an order can change: the order and its queue, the
 * manufacturing board the same states drive (MF-04 — production state IS the order's state), and
 * the consignment-invoice side, whose candidate list is what offers "Create consignment invoice".
 */
export const orderWriteKeys = (portal: string | null): unknown[][] => [
  portalKey(portal, ['orders']),
  portalKey(portal, ['manufacturing']),
  portalKey(portal, ['consignment-invoices']),
];

/**
 * How long a write may hold the button it was clicked from.
 *
 * Not a retry budget, and not a timeout on the write: the write has already succeeded by the
 * time any of this runs. It is the point past which waiting for a re-read is worse for the
 * operator than showing them the screen and letting the re-read arrive on its own.
 */
export const SETTLE_DEADLINE_MS = 4_000;

/**
 * `work`, except it can never outlive `deadlineMs` and never rejects.
 *
 * A rejected re-read is not the operator's problem either: the write succeeded, and a failed
 * refetch belongs on the screen as that query's own error state, not as a button that is dead
 * for the rest of the session.
 *
 * Exported for the one invalidation that selects by PREDICATE rather than by key prefix
 * (A-30's bulk import, which re-reads whatever in the catalog the upload touched). It cannot
 * go through `invalidateAll`, which takes keys — but it holds a button exactly the same way,
 * so it takes the same deadline.
 */
export const capped = (work: Promise<unknown>, deadlineMs: number = SETTLE_DEADLINE_MS): Promise<void> => new Promise<void>((resolve) => {
  const timer = setTimeout(resolve, deadlineMs);
  const settle = () => { clearTimeout(timer); resolve(); };
  void work.then(settle, settle);
});

/**
 * Re-read every one of them, resolving once they have all landed — or once `deadlineMs` has
 * passed, whichever comes first. `deadlineMs` is a seam for tests; no screen passes it.
 */
export const invalidateAll = (
  qc: QueryClient,
  keys: readonly unknown[][],
  deadlineMs: number = SETTLE_DEADLINE_MS,
): Promise<void> => capped(Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey }))), deadlineMs);

/** A write's re-read, split by what the acting screen has to show before its button comes back. */
export interface WriteReread {
  /** Awaited. The acting screen's own entries, and only those. */
  readonly settle: readonly unknown[][];
  /** Invalidated, never awaited. Everything else the write touched. */
  readonly background: readonly unknown[][];
}

/** Does `prefix` match the start of `key`, the way `invalidateQueries` matches one? */
const covers = (prefix: readonly unknown[], key: readonly unknown[]): boolean =>
  prefix.length <= key.length && prefix.every((part, i) => part === key[i]);

/**
 * A-15, the admin order detail. What its buttons are read from: the order itself — a prefix, so
 * the payments, milestones and invoice entries hanging off it come with it — and the
 * consignment-invoice candidates, which decide whether "Create consignment invoice" is offered at all
 * (defect 1's named casualty). Nothing else on this screen draws a button.
 */
export const orderDetailWrite = (portal: string | null, orderId: string): WriteReread => ({
  settle: [portalKey(portal, ['orders', orderId]), portalKey(portal, ['consignment-invoices', 'candidates'])],
  background: orderWriteKeys(portal),
});

/** A-26, the manufacturing board. Every row and button on it is drawn from the board's own query. */
export const manufacturingBoardWrite = (portal: string | null): WriteReread => ({
  settle: [manufacturingBoardKey(portal)],
  background: orderWriteKeys(portal),
});

/**
 * Everything a write against a consignment invoice or the shipment under it can change: the invoice,
 * its list and its candidate scan; the shipment; and the orders, whose claimable quantity moves
 * with the invoice and whose status moves with the shipment (dispatch and deliver cascade into
 * `OrderService.transition()` server-side).
 *
 * One statement, because there were three copies of this list — `invalidateConsignmentInvoices`,
 * `useShipmentAction` and `useUpdateShipmentDetails` each carried their own, in two different
 * orders. That is defect (1) waiting to happen a second time.
 */
export const consignmentInvoiceWriteKeys = (portal: string | null): unknown[][] => [
  portalKey(portal, ['consignment-invoices']),
  portalKey(portal, ['shipments']),
  portalKey(portal, ['orders']),
];

/**
 * A-19, the consignment invoice detail — and the shipment panel inside it.
 *
 * One query draws the whole screen. The shipment has no screen of its own: it arrives inside the
 * invoice's payload, so the invoice is what has to be on screen before Dispatch, Create shipment
 * or Save courier comes back. That is why the two shipment hooks are told which invoice they are
 * inside; settling on the shipment's own key instead would hand the button back over an unchanged
 * `OPEN` badge, which is defect (2) again.
 *
 * `['consignment-invoices', id]` does not match the candidate scan at
 * `['consignment-invoices', 'candidates', …]` — the second segment differs — so the slowest query on
 * this side of the app is re-read in the background, where it belongs.
 */
export const consignmentInvoiceWrite = (portal: string | null, invoiceId: string | undefined): WriteReread => ({
  settle: [portalKey(portal, ['consignment-invoices', invoiceId ?? ''])],
  background: consignmentInvoiceWriteKeys(portal),
});

/**
 * A-20, creating a consignment invoice — and the one write here that awaits nothing, deliberately.
 *
 * The screen navigates to the new invoice the moment this resolves, so there is no screen left to
 * show a stale answer on: the invoice it lands on has no cache entry yet and fetches itself, and
 * the list behind it refetches when it is next mounted. Defect (2) was a button re-offering an
 * action over an unchanged screen; a screen that is being replaced cannot do that.
 *
 * What it must NOT await is the only query this screen has — `['consignment-invoices', 'candidates',
 * 'all']`, the unpaged scan of every READY order with unclaimed lines. That is the slowest query
 * in the admin console, it is about to be unmounted, and waiting for it is what left
 * "Create consignment invoice" greyed out over an invoice the database already had.
 */
export const consignmentInvoiceCreateWrite = (portal: string | null): WriteReread => ({
  settle: [],
  background: consignmentInvoiceWriteKeys(portal),
});

/**
 * Every cached answer for one portal — the broadest admin prefix there is. SC-02 still holds:
 * another portal's entries, and the retailer's, are not under it.
 *
 * Two screens re-read this much because what they change really does move figures everywhere.
 * Neither may AWAIT it: the admin shell mounts its notification bell and its "Waiting for your
 * approval" banner on every page, both polled, and both sit under this prefix. A button waiting
 * on the banner is a button waiting on chrome it does not render.
 */
export const portalSubtree = (portal: string | null): unknown[] => portalKey(portal, []);

/** A-27, account wind-down. The screen shows its lifecycle list and one account's detail, both under one prefix. */
export const lifecycleWrite = (portal: string | null): WriteReread => ({
  settle: [portalKey(portal, ['lifecycle'])],
  background: [portalSubtree(portal)],
});

/** A-28, the stock desk. Its overview, each retailer's held pieces and the loss queue all sit under `stock`. */
export const stockDeskWrite = (portal: string | null): WriteReread => ({
  settle: [portalKey(portal, ['stock'])],
  background: [portalSubtree(portal)],
});

/**
 * Start the whole re-read; wait only for the acting screen's half of it.
 *
 * Each cached query is invalidated once — `background` skips what `settle` has already claimed —
 * so the split costs no extra request, and `settle` together with `background` is still the one
 * complete statement of what the write touched.
 */
export const settleWrite = (
  qc: QueryClient,
  reread: WriteReread,
  deadlineMs: number = SETTLE_DEADLINE_MS,
): Promise<void> => {
  const settled = invalidateAll(qc, reread.settle, deadlineMs);
  for (const queryKey of reread.background) {
    void qc.invalidateQueries({
      queryKey,
      predicate: (q) => !reread.settle.some((claimed) => covers(claimed, q.queryKey)),
    }).catch(() => undefined);
  }
  return settled;
};

/* ------------------------------------------------------------------------------------
 * D97 Wave 0 — the old names, kept alive for one wave only.
 *
 * The three cache-key files (`hooks.ts`, this file, `write-keys.spec.ts`) must agree on one
 * string and `tsc` checks none of it: a half-rename here does not fail, it silently stops
 * invalidating and the UI shows a stale invoice after a write. That is why the STRING moved in
 * one commit and only the IDENTIFIERS are aliased.
 *
 * Wave 1's four sessions each migrate their own call sites against a green typecheck; none of
 * them may edit this file. **Wave 3 deletes everything below this line, in the same release**,
 * and the vocabulary gate enforces it — these names are allow-listed as transitional entries,
 * and an allow-list entry that matches nothing is itself a gate failure (exit 2).
 * ----------------------------------------------------------------------------------- */

